import { Box, Divider, Popover } from "@mui/material";
import type { Ref } from "react";
import { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import PdfDocumentWrapper from "@entrilia/shared/components/previewFile/pdfViewer/PdfDocumentWrapper";
import PdfPagesContainer from "@entrilia/shared/components/previewFile/pdfViewer/PdfPagesContainer";
import PdfThumbnailsContainer from "@entrilia/shared/components/previewFile/pdfViewer/PdfThumbnailsContainer";
import PdfViewerControls from "@entrilia/shared/components/previewFile/pdfViewer/PdfViewerControls";
import { usePdfViewer } from "@entrilia/shared/components/previewFile/pdfViewer/pdfViewerState";
import { pdfViewerStyles } from "@entrilia/shared/components/previewFile/pdfViewer/pdfViewerStyles";
import PdfCitationPage from "./PdfCitationPage";
import type { CitationHighlight } from "./citationHighlightsHelper";

export interface PdfHighlight extends CitationHighlight {
  pageNumber: number;
}

export interface PdfHighlightsPreviewHandle {
  scrollToCitation: (pageNumber: number, citationId: string) => void;
}

interface Props {
  ref?: Ref<PdfHighlightsPreviewHandle>;
  fileSrc: string;
  fileName: string;
  highlights: PdfHighlight[];
  selectedCitationId?: string;
  autoScrollToCitationId?: string;
}

const PdfHighlightsPreview = ({
  ref,
  fileSrc,
  fileName,
  highlights,
  selectedCitationId,
  autoScrollToCitationId,
}: Props) => {
  const {
    viewerState,
    containerEl,
    setContainerEl,
    pageRefs,
    containerSize,
    handleScroll,
    navigateToPage,
    toggleThumbnails,
    handleDocumentLoaded,
    updateLoadingProgress,
    zoomIn,
    zoomOut,
    rotateLeft,
    rotateRight,
  } = usePdfViewer();

  const [controlsEl, setControlsEl] = useState<HTMLDivElement | null>(null);

  const highlightsByPage = useMemo(() => {
    const map = new Map<number, CitationHighlight[]>();
    for (const { pageNumber, ...highlight } of highlights) {
      const pageHighlights = map.get(pageNumber);
      if (pageHighlights) {
        pageHighlights.push(highlight);
      } else {
        map.set(pageNumber, [highlight]);
      }
    }
    return map;
  }, [highlights]);

  const containerOrientation =
    containerSize?.height && containerSize?.width
      ? containerSize.height > containerSize.width
        ? "portrait"
        : "landscape"
      : undefined;

  const pageHeight =
    containerSize?.height && containerOrientation === "landscape" ? containerSize.height - 48 : undefined;

  const pageWidth = containerSize?.width && containerOrientation === "portrait" ? containerSize.width - 48 : undefined;

  const scrollToCitation = useCallback(
    (pageNumber: number, citationId: string) => {
      navigateToPage(pageNumber);

      requestAnimationFrame(() => {
        const pageEl = pageRefs.current?.[pageNumber - 1];
        if (!pageEl) {
          return;
        }
        const highlightEl = pageEl.querySelector(`[data-citation-id="${citationId}"]`);
        highlightEl?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },
    [navigateToPage, pageRefs]
  );

  useImperativeHandle(ref, () => ({ scrollToCitation }), [scrollToCitation]);

  useEffect(() => {
    if (!autoScrollToCitationId || !viewerState.isDocumentLoaded) {
      return;
    }

    const target = highlights.find((highlight) => highlight.citationId === autoScrollToCitationId);
    if (target) {
      scrollToCitation(target.pageNumber, autoScrollToCitationId);
    }
  }, [autoScrollToCitationId, viewerState.isDocumentLoaded, highlights, scrollToCitation]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, flexGrow: 1 }}>
      <Box ref={setControlsEl} sx={{ py: 1, px: 2.5 }}>
        <PdfViewerControls
          fileName={fileName}
          viewerState={viewerState}
          hideFileName
          onNavigateToPage={navigateToPage}
          onToggleThumbnails={toggleThumbnails}
          onZoomOut={zoomOut}
          onZoomIn={zoomIn}
          onRotateLeft={rotateLeft}
          onRotateRight={rotateRight}
        />
      </Box>
      <Divider />
      <Box ref={setContainerEl} sx={{ flex: 1, bgcolor: pdfViewerStyles.bgcolor }}>
        <ScrollableFlexContainer onScroll={handleScroll} sx={{ height: "100%" }}>
          <Box sx={{ height: "100%" }}>
            <PdfDocumentWrapper
              fileSrc={fileSrc}
              viewerState={viewerState}
              onDocumentLoad={handleDocumentLoaded}
              onLoadingProgress={updateLoadingProgress}
            >
              <Popover
                open={viewerState.showThumbnails}
                onClose={() => toggleThumbnails()}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                anchorEl={controlsEl}
                sx={{ "& .MuiPopover-paper": { maxHeight: containerSize?.height } }}
              >
                <PdfThumbnailsContainer viewerState={viewerState} />
              </Popover>

              <PdfPagesContainer
                viewerState={viewerState}
                containerEl={containerEl}
                pageRefs={pageRefs}
                pageHeight={pageHeight}
                pageWidth={pageWidth}
                renderPageContent={({ pageNumber, pageHeight, pageWidth, renderPage, renderLayers }) => (
                  <PdfCitationPage
                    pageNumber={pageNumber}
                    pageHeight={pageHeight}
                    pageWidth={pageWidth}
                    renderPage={renderPage}
                    renderLayers={renderLayers}
                    highlights={highlightsByPage.get(pageNumber) ?? []}
                    selectedCitationId={selectedCitationId}
                    rotationDegrees={viewerState.rotationDegrees}
                  />
                )}
              />
            </PdfDocumentWrapper>
          </Box>
        </ScrollableFlexContainer>
      </Box>
    </Box>
  );
};

export default PdfHighlightsPreview;
